import React, { useState, useEffect, useRef, memo, useCallback } from 'react';
import cn from 'classnames';

import Toaster from 'components/Toaster';
import { useNotification } from 'hooks/useNotification';
import { Notification } from 'lib/models/notification';

import styles from './NotificationToaster.module.scss';

export interface NotificationToasterProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
}

export const NotificationToaster: React.FC<NotificationToasterProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
  }: NotificationToasterProps) => {
    const contentClassNames = cn(styles.container, className);
    const notificationInstance = useNotification();
    const [notification, setNotification] = useState<Notification | null>(null);
    const timerRef = useRef<number | null>(null);

    const clearTimer = useCallback(() => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
      }
    }, []);

    const handleClose = () => {
      setNotification(null);
      clearTimer();
    };

    useEffect(() => {
      const subscription = notificationInstance.notifications$.subscribe(
        (notification) => {
          setNotification(notification);
          clearTimer();

          const timeout = notification.timeout || 5000;
          timerRef.current = window.setTimeout(() => {
            setNotification(null);
          }, timeout);
        }
      );

      return () => {
        subscription.unsubscribe();
      };
    }, [clearTimer]);

    if (!notification) return <></>;

    return (
      <div
        className={contentClassNames}
        style={style}
        data-testid="NotificationToaster"
      >
        <Toaster
          title={notification.title}
          message={notification.message}
          type={notification.type}
          onClose={handleClose}
        />
      </div>
    );
  }
);

NotificationToaster.displayName = 'NotificationToaster';
